import React, { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  BuildingLibraryIcon,
  HomeIcon,
  ChartBarIcon,
  TruckIcon,
  UsersIcon,
  CogIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  BellIcon,
  UserCircleIcon,
  SunIcon,
  MoonIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  WrenchScrewdriverIcon,
  ShieldCheckIcon,
  ChartPieIcon,
  BanknotesIcon,
  ClipboardDocumentListIcon,
  InformationCircleIcon,
  UserIcon,
  BellAlertIcon,
  LockClosedIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CubeIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline'
import { useAuth } from '../../contextos/autenticacion/ContextoAutenticacion'
import { useTema } from '../../contextos/tema/ContextoTema'

interface LayoutPrincipalProps {
  children: React.ReactNode
}

interface SubMenuItem {
  name: string
  href: string
  icon: React.ComponentType<any>
}

interface MenuItem {
  name: string
  href?: string
  icon: React.ComponentType<any>
  current?: boolean
  subItems?: SubMenuItem[]
}

const LayoutPrincipal: React.FC<LayoutPrincipalProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [expandedMenus, setExpandedMenus] = useState<string[]>([])
  const { usuario, logout } = useAuth()
  const { toggleTema, isDark } = useTema()
  const location = useLocation()
  const navigate = useNavigate()

  const navigation: MenuItem[] = [
    { name: 'Inicio', href: '/home', icon: HomeIcon },
    { name: 'Dashboard', href: '/dashboard', icon: ChartBarIcon },
    { name: 'Normatividad', href: '/dashboard/normatividad', icon: BuildingLibraryIcon },
    {
      name: 'Tarifas',
      icon: BanknotesIcon,
      subItems: [
        { name: 'Clasificación LIGIE', href: '/tarifas/clasificacion', icon: MagnifyingGlassIcon }
      ]
    },
    {
      name: 'Logística',
      icon: CubeIcon,
      subItems: [
        { name: 'Gestión de Envíos', href: '/dashboard/envios', icon: ClipboardDocumentListIcon },
        { name: 'Seguimiento en Vivo', href: '/operaciones/seguimiento', icon: MapPinIcon },
        { name: 'Análisis y Reportes', href: '/operaciones/historial', icon: ChartPieIcon }
      ]
    },
    {
      name: 'Flota',
      icon: TruckIcon,
      subItems: [
        { name: 'Vehículos', href: '/flota', icon: TruckIcon },
        { name: 'Conductores', href: '/flota/conductores', icon: UsersIcon },
        { name: 'Mantenimiento', href: '/flota/mantenimiento', icon: WrenchScrewdriverIcon },
        { name: 'Licencias y Permisos', href: '/permisos/licencias', icon: ShieldCheckIcon }
      ]
    },
    {
      name: 'Reportes',
      icon: ChartPieIcon,
      subItems: [
        { name: 'Rendimiento', href: '/reportes/rendimiento', icon: ChartBarIcon },
        { name: 'Financiero', href: '/reportes/financiero', icon: BanknotesIcon },
        { name: 'Operacional', href: '/reportes/operacional', icon: ClipboardDocumentListIcon }
      ]
    },
    {
      name: 'Configuración',
      icon: CogIcon,
      subItems: [
        { name: 'Perfil', href: '/configuracion/perfil', icon: UserIcon },
        { name: 'Notificaciones', href: '/configuracion/notificaciones', icon: BellAlertIcon },
        { name: 'Seguridad', href: '/configuracion/seguridad', icon: LockClosedIcon }
      ]
    },
    { name: 'Información', href: '/informacion', icon: InformationCircleIcon }
  ]

  const toggleMenu = (menuName: string) => {
    setExpandedMenus(prev => 
      prev.includes(menuName) 
        ? prev.filter(name => name !== menuName)
        : [...prev, menuName]
    )
  }

  const isMenuExpanded = (menuName: string) => {
    return expandedMenus.includes(menuName)
  }

  const isCurrentPath = (href: string) => {
    return location.pathname === href || location.pathname.startsWith(href + '/')
  }

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/login')
    } catch (error) {
      console.error('Error al cerrar sesión:', error)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      {/* Sidebar móvil */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-80 lg:hidden"
              onClick={() => setSidebarOpen(false)}
            />
            <motion.div
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 shadow-xl lg:hidden"
            >
              <div className="flex h-16 items-center justify-between px-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                      <TruckIcon className="h-5 w-5 text-white" />
                    </div>
                    <span className="ml-2 text-xl font-bold text-gray-900 dark:text-white">LogiXport</span>
                  </div>
                  <button
                    onClick={() => setSidebarOpen(false)}
                    className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:text-gray-300 dark:hover:text-gray-200 dark:hover:bg-gray-700"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
              <nav className="mt-5 px-2">
                <div className="space-y-1">
                {navigation.map((item) => (
                  <div key={item.name}>
                    {item.href ? (
                      <Link
                        to={item.href}
                        onClick={() => setSidebarOpen(false)}
                        className={`${
                          isCurrentPath(item.href)
                            ? 'bg-blue-50 dark:bg-blue-900/20 border-r-2 border-blue-600 text-blue-700 dark:text-blue-400'
                            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                        } group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-200`}
                      >
                        <item.icon
                          className={`${
                            isCurrentPath(item.href) ? 'text-blue-500 dark:text-blue-400' : 'text-gray-400 dark:text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300'
                          } mr-3 h-6 w-6`}
                        />
                        {item.name}
                      </Link>
                    ) : (
                      <>
                        <button
                          onClick={() => toggleMenu(item.name)}
                          className="w-full group flex items-center justify-between px-2 py-2 text-sm font-medium rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white transition-colors duration-200"
                        >
                          <div className="flex items-center">
                            <item.icon className="mr-3 h-6 w-6 text-gray-400 dark:text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300" />
                            {item.name}
                          </div>
                          {isMenuExpanded(item.name) ? (
                            <ChevronDownIcon className="h-4 w-4" />
                          ) : (
                            <ChevronRightIcon className="h-4 w-4" />
                          )}
                        </button>
                        <AnimatePresence>
                          {isMenuExpanded(item.name) && item.subItems && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <div className="ml-6 mt-1 space-y-1">
                                {item.subItems.map((subItem) => (
                                  <Link
                                    key={subItem.name}
                                    to={subItem.href}
                                    onClick={() => setSidebarOpen(false)}
                                    className={`${
                                      isCurrentPath(subItem.href)
                                        ? 'bg-blue-50 dark:bg-blue-900/20 border-r-2 border-blue-600 text-blue-700 dark:text-blue-400'
                                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                                    } group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-200`}
                                  >
                                    <subItem.icon
                                      className={`${
                                        isCurrentPath(subItem.href) ? 'text-blue-500 dark:text-blue-400' : 'text-gray-400 dark:text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300'
                                      } mr-3 h-5 w-5`}
                                    />
                                    {subItem.name}
                                  </Link>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </>
                    )}
                  </div>
                ))}
              </div>
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Sidebar desktop */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex min-h-0 flex-1 flex-col bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700">
          <div className="flex h-16 flex-shrink-0 items-center px-4 border-b border-gray-200 dark:border-gray-700">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
              <TruckIcon className="h-5 w-5 text-white" />
            </div>
            <span className="ml-2 text-xl font-bold text-gray-900 dark:text-white">LogiXport</span>
          </div>
          <div className="flex flex-1 flex-col overflow-y-auto">
            <nav className="flex-1 px-2 py-4">
              <div className="space-y-1">
                {navigation.map((item) => (
                  <div key={item.name}>
                    {item.href ? (
                      <Link
                        to={item.href}
                        className={`${
                          isCurrentPath(item.href)
                            ? 'bg-blue-50 dark:bg-blue-900/20 border-r-2 border-blue-600 text-blue-700 dark:text-blue-400'
                            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                        } group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-200`}
                      >
                        <item.icon
                          className={`${
                            isCurrentPath(item.href) ? 'text-blue-500 dark:text-blue-400' : 'text-gray-400 dark:text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300'
                          } mr-3 h-6 w-6`}
                        />
                        {item.name}
                      </Link>
                    ) : (
                      <>
                        <button
                          onClick={() => toggleMenu(item.name)}
                          className="w-full group flex items-center justify-between px-2 py-2 text-sm font-medium rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white transition-colors duration-200"
                        >
                          <div className="flex items-center">
                            <item.icon className="mr-3 h-6 w-6 text-gray-400 dark:text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300" />
                            {item.name}
                          </div>
                          {isMenuExpanded(item.name) ? (
                            <ChevronDownIcon className="h-4 w-4" />
                          ) : (
                            <ChevronRightIcon className="h-4 w-4" />
                          )}
                        </button>
                        <AnimatePresence>
                          {isMenuExpanded(item.name) && item.subItems && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <div className="ml-6 mt-1 space-y-1">
                                {item.subItems.map((subItem) => (
                                  <Link
                                    key={subItem.name}
                                    to={subItem.href}
                                    className={`${
                                      isCurrentPath(subItem.href)
                                        ? 'bg-blue-50 dark:bg-blue-900/20 border-r-2 border-blue-600 text-blue-700 dark:text-blue-400'
                                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                                    } group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-200`}
                                  >
                                    <subItem.icon
                                      className={`${
                                        isCurrentPath(subItem.href) ? 'text-blue-500 dark:text-blue-400' : 'text-gray-400 dark:text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300'
                                      } mr-3 h-5 w-5`}
                                    />
                                    {subItem.name}
                                  </Link>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </nav>
          </div>
        </div>
      </div>

      {/* Contenido principal */}
      <div className="lg:pl-64 flex flex-col flex-1">
        {/* Header */}
        <div className="sticky top-0 z-30 flex h-16 flex-shrink-0 bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
          <button
            type="button"
            className="border-r border-gray-200 dark:border-gray-700 px-4 text-gray-500 dark:text-gray-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          <div className="flex flex-1 justify-between px-4">
            <div className="flex flex-1">
              {/* Breadcrumb o título de página */}
              <div className="flex items-center">
                <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {navigation.find(item => item.href && isCurrentPath(item.href))?.name || 
                   navigation.find(item => item.subItems?.some(subItem => isCurrentPath(subItem.href)))?.name ||
                   'Panel de Usuario'}
                </h1>
              </div>
            </div>
            <div className="ml-4 flex items-center md:ml-6 space-x-2">
              {/* Toggle de tema */}
              <button
                onClick={toggleTema}
                className="rounded-full bg-white dark:bg-gray-700 p-2 text-gray-400 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-colors duration-200"
                title={isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
              >
                {isDark ? (
                  <SunIcon className="h-5 w-5" />
                ) : (
                  <MoonIcon className="h-5 w-5" />
                )}
              </button>

              {/* Notificaciones */}
              <button
                type="button"
                className="rounded-full bg-white dark:bg-gray-700 p-2 text-gray-400 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
              >
                <BellIcon className="h-5 w-5" />
              </button>

              {/* Menú de usuario */}
              <div className="relative ml-3">
                <div className="flex items-center space-x-3">
                  <div className="hidden md:block text-right">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">{usuario?.nombre || 'Usuario'}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{usuario?.email}</div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <UserCircleIcon className="h-8 w-8 text-gray-400 dark:text-gray-300" />
                    <button
                      onClick={handleLogout}
                      className="flex items-center space-x-1 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors duration-200"
                      title="Cerrar sesión"
                    >
                      <ArrowRightOnRectangleIcon className="h-4 w-4" />
                      <span className="hidden sm:inline">Salir</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contenido de la página */}
        <main className="flex-1 min-h-0">
          <div className="h-full py-4 lg:py-6">
            <div className="w-full px-4 sm:px-6 lg:px-8 h-full">
              <div className="h-full">
                {children}
              </div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 mt-auto">
          <div className="w-full px-4 sm:px-6 lg:px-8">
            <div className="py-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {/* Información de la empresa */}
                <div className="md:col-span-2">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                      <TruckIcon className="h-5 w-5 text-white" />
                    </div>
                    <span className="ml-2 text-xl font-bold text-gray-900 dark:text-white">LogiXport</span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Solución integral para la gestión logística y transporte de mercancías. 
                    Optimizamos tus operaciones con tecnología de vanguardia.
                  </p>
                  <div className="flex space-x-4">
                    <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                      <PhoneIcon className="h-4 w-4 mr-2" />
                      +1 (555) 123-4567
                    </div>
                    <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                      <EnvelopeIcon className="h-4 w-4 mr-2" />
                      info@logixport.com
                    </div>
                  </div>
                </div>

                {/* Enlaces rápidos */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Enlaces Rápidos</h3>
                  <ul className="space-y-2">
                    <li>
                      <Link to="/dashboard" className="text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200">
                        Dashboard
                      </Link>
                    </li>
                    <li>
                      <Link to="/dashboard/envios" className="text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200">
                        Gestión de Envíos
                      </Link>
                    </li>
                    <li>
                      <Link to="/flota" className="text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200">
                        Flota de Vehículos
                      </Link>
                    </li>
                    <li>
                      <Link to="/flota/conductores" className="text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200">
                        Conductores
                      </Link>
                    </li>
                  </ul>
                </div>

                {/* Soporte */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Soporte</h3>
                  <ul className="space-y-2">
                    <li>
                      <Link to="/configuracion/perfil" className="text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200">
                        Centro de Ayuda
                      </Link>
                    </li>
                    <li>
                      <Link to="/dashboard/normatividad" className="text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200">
                        Documentación
                      </Link>
                    </li>
                    <li>
                      <Link to="/configuracion/perfil" className="text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200">
                        Contacto Técnico
                      </Link>
                    </li>
                    <li>
                      <Link to="/dashboard" className="text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200">
                        Estado del Sistema
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Línea divisoria y copyright */}
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="flex flex-col md:flex-row justify-between items-center">
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    © {new Date().getFullYear()} LogiXport. Todos los derechos reservados.
                    <span className="hidden md:inline"> | </span>
                    <br className="md:hidden" />
                    Powered by <a href="https://vespex.pro" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:text-cyan-500 dark:text-cyan-400 transition-colors font-medium">VESPEX PRO</a>
                  </div>
                  <div className="flex items-center mt-2 md:mt-0">
                    <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                      <MapPinIcon className="h-4 w-4 mr-1" />
                      Ciudad de México, México
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  )
}

export default LayoutPrincipal
